import { useTranslation } from '@repo/i18n'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { GooeyInput } from '#aceternity/gooey-input'

function buildSearchPatch(
	prev: Record<string, unknown>,
	next: string | undefined,
	resetKeys: readonly string[] | undefined,
	searchKey: string
): Record<string, unknown> {
	const patch: Record<string, unknown> = {
		...prev,
		[searchKey]: next || undefined
	}
	for (const key of resetKeys ?? []) {
		patch[key] = undefined
	}
	return patch
}

/**
 * Debounced search for list pages, drawn as the Aceternity gooey pill: it rests
 * as a short "search" chip and opens into the field on click, with its own
 * clear button (Escape clears too).
 * Reads/writes `searchKey` (the `search` URL param by default) itself. While typing, local state drives
 * the input; the URL is updated after a short delay. External URL
 * changes are reflected back into the input via derived-state sync (no effects).
 *
 * Pass `resetKeys` to strip companion URL params (e.g. pagination cursors)
 * whenever the search value changes, so the list always re-queries from the
 * start. Pass `maxLength` when the backend caps the search it accepts.
 */
export function ListInputSearch(props: {
	placeholder?: string
	resetKeys?: readonly string[]
	collapsedWidth?: number
	expandedWidth?: number
	maxLength?: number
	searchKey?: string
}) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const searchParams: Record<string, unknown> = useSearch({ strict: false })
	const searchKey = props.searchKey ?? 'search'
	const searchValue = searchParams[searchKey]
	const externalSearch = typeof searchValue === 'string' ? searchValue : ''

	const [value, setValue] = useState(externalSearch)
	const [lastExternal, setLastExternal] = useState(externalSearch)
	const pendingSearch = useRef<number | undefined>(undefined)
	// A queued search belongs to this list. Navigating away must not apply it
	// to the destination route's filters.
	useEffect(() => () => window.clearTimeout(pendingSearch.current), [])

	if (externalSearch !== lastExternal) {
		setLastExternal(externalSearch)
		setValue(externalSearch)
	}

	const handleChange = (next: string) => {
		setValue(next)
		window.clearTimeout(pendingSearch.current)
		pendingSearch.current = window.setTimeout(() => {
			navigate({
				to: '.',
				search: (prev: Record<string, unknown>) =>
					buildSearchPatch(prev, next, props.resetKeys, searchKey),
				replace: true
			})
		}, 750)
	}

	return (
		<GooeyInput
			clearLabel={t('shared:list_input_search.clear')}
			collapsedLabel={t('shared:list_input_search.label')}
			collapsedWidth={props.collapsedWidth}
			expandedWidth={props.expandedWidth}
			maxLength={props.maxLength}
			onValueChange={handleChange}
			placeholder={props.placeholder}
			value={value}
		/>
	)
}
